import { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Square, Hexagon, Undo2, Trash2, Library as LibraryIcon, ChevronUp, ChevronDown, Eye, EyeOff, MousePointer2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "wouter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuthSession, useCreateCloudSvg } from "@/features/space-designer/lib/use-space-svg";
import { useSvgLibrary } from "@/features/space-designer/lib/svg-library";
import { sanitizeSvg } from "@/features/space-designer/lib/svg-sanitize";
import { CATALOG } from "@/features/space-designer/lib/catalog";

const ROLE_OPTIONS = [
  "Table",
  "Storage",
  "Chair",
  "Sofa",
  "Desk",
  "Shelf",
  "Pillar",
  "Equipment",
  "Other",
] as const;
type Role = (typeof ROLE_OPTIONS)[number];

type ShapeBase = { id: string; z: number; hidden?: boolean; role: Role; fill: string; stroke: string };
type Shape =
  | (ShapeBase & { kind: "rect"; x: number; y: number; w: number; h: number })
  | (ShapeBase & { kind: "polygon"; points: { x: number; y: number }[] })
  | (ShapeBase & { kind: "svg"; x: number; y: number; w: number; h: number; inner: string; viewBox: string; name: string });

const CANVAS_W = 480;
const CANVAS_H = 360;
const GRID = 20;
const FT_TO_CM = 30.48;
const IN_TO_CM = 2.54;
const uid = () => Math.random().toString(36).slice(2, 10);

function cmToFtIn(cm: number) {
  const totalIn = cm / IN_TO_CM;
  const ft = Math.floor(totalIn / 12);
  const inch = Math.round((totalIn - ft * 12) * 10) / 10;
  return { ft, in: inch };
}
function ftInToCm(ft: number, inch: number) {
  return Math.max(1, Math.round((ft * FT_TO_CM + inch * IN_TO_CM) * 10) / 10);
}

export function DrawComponent({ trigger }: { trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [tool, setTool] = useState<"select" | "rect" | "polygon">("select");
  const [fill, setFill] = useState("#e2e8f0");
  const [stroke, setStroke] = useState("#0f172a");
  const [role, setRole] = useState<Role>("Table");
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [rectStart, setRectStart] = useState<{ x: number; y: number } | null>(null);
  const [rectCurr, setRectCurr] = useState<{ x: number; y: number } | null>(null);
  const [polyPts, setPolyPts] = useState<{ x: number; y: number }[]>([]);
  const [hoverPt, setHoverPt] = useState<{ x: number; y: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [units, setUnits] = useState<"ft" | "cm">("ft");
  const [drag, setDrag] = useState<
    | null
    | { id: string; mode: "move"; start: { x: number; y: number }; orig: { x: number; y: number } }
    | { id: string; mode: "resize"; corner: "nw" | "ne" | "sw" | "se"; start: { x: number; y: number }; orig: { x: number; y: number; w: number; h: number } }
  >(null);

  const [name, setName] = useState("Custom Component");
  const [cmW, setCmW] = useState(100);
  const [cmH, setCmH] = useState(100);
  const [snap, setSnap] = useState(true);

  const svgRef = useRef<SVGSVGElement>(null);
  const { session } = useAuthSession();
  const create = useCreateCloudSvg();
  const addLocal = useSvgLibrary((s) => s.addAsset);

  function reset() {
    setShapes([]);
    setRectStart(null);
    setRectCurr(null);
    setPolyPts([]);
    setSelectedId(null);
  }

  const nextZ = () => (shapes.length ? Math.max(...shapes.map((s) => s.z)) + 1 : 1);

  function toLocal(e: React.MouseEvent): { x: number; y: number } {
    const svg = svgRef.current!;
    const rect = svg.getBoundingClientRect();
    let x = ((e.clientX - rect.left) / rect.width) * CANVAS_W;
    let y = ((e.clientY - rect.top) / rect.height) * CANVAS_H;
    if (snap) {
      x = Math.round(x / GRID) * GRID;
      y = Math.round(y / GRID) * GRID;
    }
    return { x: Math.max(0, Math.min(CANVAS_W, x)), y: Math.max(0, Math.min(CANVAS_H, y)) };
  }

  function rawLocal(e: React.MouseEvent): { x: number; y: number } {
    const svg = svgRef.current!;
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((e.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  }

  function hitTest(p: { x: number; y: number }): Shape | null {
    // topmost first
    for (let i = orderedShapes.length - 1; i >= 0; i--) {
      const s = orderedShapes[i];
      if (s.hidden) continue;
      if (s.kind === "rect" || s.kind === "svg") {
        if (p.x >= s.x && p.x <= s.x + s.w && p.y >= s.y && p.y <= s.y + s.h) return s;
      } else {
        // polygon point-in-poly
        const pts = s.points;
        let inside = false;
        for (let j = 0, k = pts.length - 1; j < pts.length; k = j++) {
          const xi = pts[j].x, yi = pts[j].y, xj = pts[k].x, yj = pts[k].y;
          if ((yi > p.y) !== (yj > p.y) && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi + 0.0001) + xi) inside = !inside;
        }
        if (inside) return s;
      }
    }
    return null;
  }

  function onMouseDown(e: React.MouseEvent) {
    if (tool === "select") {
      const p = rawLocal(e);
      const hit = hitTest(p);
      if (hit) {
        setSelectedId(hit.id);
        if (hit.kind === "rect" || hit.kind === "svg") {
          setDrag({ id: hit.id, mode: "move", start: p, orig: { x: hit.x, y: hit.y } });
        }
      } else {
        setSelectedId(null);
      }
      return;
    }
    const p = toLocal(e);
    if (tool === "rect") {
      setRectStart(p);
      setRectCurr(p);
    } else {
      // first click closes if near first point
      if (polyPts.length >= 3) {
        const first = polyPts[0];
        const dx = p.x - first.x;
        const dy = p.y - first.y;
        if (Math.hypot(dx, dy) < GRID * 0.8) {
          setShapes((s) => [...s, { id: uid(), z: nextZ(), kind: "polygon", points: polyPts, fill, stroke, role }]);
          setPolyPts([]);
          return;
        }
      }
      setPolyPts((pts) => [...pts, p]);
    }
  }

  function onMouseMove(e: React.MouseEvent) {
    if (drag) {
      const raw = rawLocal(e);
      const dx = raw.x - drag.start.x;
      const dy = raw.y - drag.start.y;
      if (drag.mode === "move") {
        let nx = drag.orig.x + dx;
        let ny = drag.orig.y + dy;
        if (snap) { nx = Math.round(nx / GRID) * GRID; ny = Math.round(ny / GRID) * GRID; }
        updateShape(drag.id, { x: nx, y: ny } as Partial<Shape>);
      } else {
        const { orig, corner } = drag;
        let x = orig.x, y = orig.y, w = orig.w, h = orig.h;
        if (corner === "se") { w = orig.w + dx; h = orig.h + dy; }
        else if (corner === "ne") { y = orig.y + dy; w = orig.w + dx; h = orig.h - dy; }
        else if (corner === "sw") { x = orig.x + dx; w = orig.w - dx; h = orig.h + dy; }
        else { x = orig.x + dx; y = orig.y + dy; w = orig.w - dx; h = orig.h - dy; }
        if (snap) { x = Math.round(x / GRID) * GRID; y = Math.round(y / GRID) * GRID; w = Math.round(w / GRID) * GRID; h = Math.round(h / GRID) * GRID; }
        if (w < 8) w = 8; if (h < 8) h = 8;
        updateShape(drag.id, { x, y, w, h } as Partial<Shape>);
      }
      return;
    }
    const p = toLocal(e);
    setHoverPt(p);
    if (tool === "rect" && rectStart) setRectCurr(p);
  }

  function onMouseUp() {
    if (drag) { setDrag(null); return; }
    if (tool === "rect" && rectStart && rectCurr) {
      const x = Math.min(rectStart.x, rectCurr.x);
      const y = Math.min(rectStart.y, rectCurr.y);
      const w = Math.abs(rectCurr.x - rectStart.x);
      const h = Math.abs(rectCurr.y - rectStart.y);
      if (w >= 4 && h >= 4) setShapes((s) => [...s, { id: uid(), z: nextZ(), kind: "rect", x, y, w, h, fill, stroke, role }]);
      setRectStart(null);
      setRectCurr(null);
    }
  }

  function onDoubleClick() {
    if (tool === "polygon" && polyPts.length >= 3) {
      setShapes((s) => [...s, { id: uid(), z: nextZ(), kind: "polygon", points: polyPts, fill, stroke, role }]);
      setPolyPts([]);
    }
  }

  function undo() {
    if (polyPts.length > 0) {
      setPolyPts((pts) => pts.slice(0, -1));
    } else {
      setShapes((s) => s.slice(0, -1));
    }
  }

  function insertCatalogShape(entryId: string) {
    const c = CATALOG.find((x) => x.id === entryId);
    if (!c) return;
    // Scale catalog cm dims into canvas px so it visibly fits.
    const maxW = CANVAS_W * 0.5;
    const maxH = CANVAS_H * 0.5;
    const scale = Math.min(maxW / c.w, maxH / c.h, 1.5);
    const w = Math.max(20, c.w * scale);
    const h = Math.max(20, c.h * scale);
    const x = Math.max(0, (CANVAS_W - w) / 2);
    const y = Math.max(0, (CANVAS_H - h) / 2);
    const roleMap: Record<string, Role> = {
      furniture: "Table", storage: "Storage", fixture: "Equipment",
      architectural: "Pillar", equipment: "Equipment", room: "Other",
    };
    const r: Role = c.kind === "furniture" && /chair/i.test(c.label) ? "Chair"
      : c.kind === "furniture" && /sofa/i.test(c.label) ? "Sofa"
      : c.kind === "furniture" && /desk/i.test(c.label) ? "Desk"
      : c.kind === "furniture" && /shelf|book/i.test(c.label) ? "Shelf"
      : roleMap[c.kind] ?? "Other";
    setShapes((s) => [...s, {
      id: uid(), z: nextZ(), kind: "rect", x, y, w, h,
      fill: c.color ?? fill, stroke, role: r,
    }]);
  }

  function insertSvgShape(asset: { inner: string; viewBox: string; name: string; width: number; height: number }) {
    const ratio = asset.height && asset.width ? asset.height / asset.width : 1;
    const w = Math.min(CANVAS_W * 0.5, 200);
    const h = w * ratio;
    setShapes((s) => [...s, {
      id: uid(), z: nextZ(), kind: "svg", name: asset.name,
      x: (CANVAS_W - w) / 2, y: (CANVAS_H - h) / 2, w, h,
      inner: asset.inner, viewBox: asset.viewBox,
      fill, stroke, role: "Other",
    }]);
  }

  function moveLayer(id: string, dir: -1 | 1) {
    setShapes((arr) => {
      const sorted = [...arr].sort((a, b) => a.z - b.z);
      const idx = sorted.findIndex((s) => s.id === id);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= sorted.length) return arr;
      const a = sorted[idx], b = sorted[swap];
      return arr.map((s) => s.id === a.id ? { ...s, z: b.z } : s.id === b.id ? { ...s, z: a.z } : s);
    });
  }
  function toggleHide(id: string) {
    setShapes((arr) => arr.map((s) => s.id === id ? { ...s, hidden: !s.hidden } : s));
  }
  function removeShape(id: string) {
    setShapes((arr) => arr.filter((s) => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  }
  function updateShape(id: string, patch: Partial<Shape>) {
    setShapes((arr) => arr.map((s) => s.id === id ? ({ ...s, ...patch } as Shape) : s));
  }

  const orderedShapes = useMemo(() => [...shapes].sort((a, b) => a.z - b.z), [shapes]);

  const innerMarkup = useMemo(() => {
    return orderedShapes
      .filter((s) => !s.hidden)
      .map((s) => {
        let cx = 0, cy = 0;
        if (s.kind === "rect") {
          cx = s.x + s.w / 2;
          cy = s.y + s.h / 2;
          const fs = Math.max(10, Math.min(20, Math.min(s.w, s.h) * 0.18));
          return `<rect x="${s.x}" y="${s.y}" width="${s.w}" height="${s.h}" fill="${s.fill}" stroke="${s.stroke}" stroke-width="2"/><text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-family="ui-sans-serif,system-ui,sans-serif" font-size="${fs}" fill="${s.stroke}" pointer-events="none">${s.role}</text>`;
        }
        if (s.kind === "svg") {
          const vb = s.viewBox.split(/\s+/).map(Number);
          const vbW = vb[2] || 1, vbH = vb[3] || 1;
          const sx = s.w / vbW, sy = s.h / vbH;
          return `<g transform="translate(${s.x} ${s.y}) scale(${sx} ${sy})">${s.inner}</g>`;
        }
        const pts = s.points.map((p) => `${p.x},${p.y}`).join(" ");
        cx = s.points.reduce((a, p) => a + p.x, 0) / s.points.length;
        cy = s.points.reduce((a, p) => a + p.y, 0) / s.points.length;
        return `<polygon points="${pts}" fill="${s.fill}" stroke="${s.stroke}" stroke-width="2"/><text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-family="ui-sans-serif,system-ui,sans-serif" font-size="14" fill="${s.stroke}" pointer-events="none">${s.role}</text>`;
      })
      .join("");
  }, [orderedShapes]);

  async function onSave() {
    if (!shapes.length) {
      toast.error("Draw at least one shape");
      return;
    }
    if (!name.trim()) {
      toast.error("Give the component a name");
      return;
    }
    const wrapped = `<svg viewBox="0 0 ${CANVAS_W} ${CANVAS_H}" xmlns="http://www.w3.org/2000/svg">${innerMarkup}</svg>`;
    const sanitized = sanitizeSvg(wrapped);
    if (!sanitized) {
      toast.error("Could not build SVG");
      return;
    }
    // Always save locally so the component is available with or without login.
    addLocal({
      name: name.trim(),
      inner: sanitized.inner,
      viewBox: `0 0 ${CANVAS_W} ${CANVAS_H}`,
      width: cmW,
      height: cmH,
      palette: sanitized.palette,
    });
    if (!session) {
      toast.success(`Saved "${name.trim()}" to your local library`);
      reset();
      setOpen(false);
      return;
    }
    try {
      const saved = await create.mutateAsync({
        name: name.trim(),
        markup: sanitized.inner,
        width: cmW,
        height: cmH,
        isShared: false,
      });
      toast.success(`Saved "${saved.name}" to your cloud library`);
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(`Saved locally — cloud sync failed: ${err instanceof Error ? err.message : "unknown"}`);
      reset();
      setOpen(false);
    }
  }

  // live preview shapes (rect drag / polygon next segment)
  const liveRect =
    tool === "rect" && rectStart && rectCurr
      ? {
          x: Math.min(rectStart.x, rectCurr.x),
          y: Math.min(rectStart.y, rectCurr.y),
          w: Math.abs(rectCurr.x - rectStart.x),
          h: Math.abs(rectCurr.y - rectStart.y),
        }
      : null;

  const libraryAssets = useSvgLibrary((s) => s.assets);
  const cmWInFt = cmToFtIn(cmW);
  const cmHInFt = cmToFtIn(cmH);
  const selected = shapes.find((s) => s.id === selectedId) ?? null;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" /> Draw a component</DialogTitle>
        </DialogHeader>
        <div className="flex gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex gap-1">
              <Button size="sm" variant={tool === "rect" ? "default" : "outline"} onClick={() => { setTool("rect"); setPolyPts([]); }} title="Rectangle">
                <Square className="w-4 h-4" />
              </Button>
              <Button size="sm" variant={tool === "polygon" ? "default" : "outline"} onClick={() => { setTool("polygon"); setRectStart(null); setRectCurr(null); }} title="Polygon">
                <Hexagon className="w-4 h-4" />
              </Button>
              <Button size="sm" variant={tool === "select" ? "default" : "outline"} onClick={() => { setTool("select"); setRectStart(null); setRectCurr(null); setPolyPts([]); }} title="Select / move / resize">
                <MousePointer2 className="w-4 h-4" />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="outline" title="Insert preset"><LibraryIcon className="w-4 h-4" /></Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2 max-h-80 overflow-auto">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Catalog</div>
                  <div className="grid gap-0.5 mb-2">
                    {CATALOG.filter((c) => c.kind !== "room").map((c) => (
                      <button key={c.id} onClick={() => insertCatalogShape(c.id)}
                        className="text-left text-xs px-2 py-1 rounded hover:bg-accent">{c.label}</button>
                    ))}
                  </div>
                  {libraryAssets.length > 0 && (
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">My SVGs</div>
                      <div className="grid gap-0.5">
                        {libraryAssets.map((a) => (
                          <button key={a.id} onClick={() => insertSvgShape(a)}
                            className="text-left text-xs px-2 py-1 rounded hover:bg-accent">{a.name}</button>
                        ))}
                      </div>
                    </>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Fill</Label>
              <input type="color" value={fill} onChange={(e) => setFill(e.target.value)} className="w-full h-8 rounded border border-border bg-transparent" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Stroke</Label>
              <input type="color" value={stroke} onChange={(e) => setStroke(e.target.value)} className="w-full h-8 rounded border border-border bg-transparent" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Label</Label>
              <Select value={role} onValueChange={(v) => setRole(v as Role)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={snap} onChange={(e) => setSnap(e.target.checked)} /> Snap to grid
            </label>
            <Button size="sm" variant="outline" onClick={undo} className="gap-1"><Undo2 className="w-3.5 h-3.5" /> Undo</Button>
            <Button size="sm" variant="ghost" onClick={reset} className="gap-1 text-destructive"><Trash2 className="w-3.5 h-3.5" /> Clear</Button>
            <div className="text-[11px] text-muted-foreground leading-snug mt-1">
              {tool === "rect"
                ? "Click & drag to draw a rectangle."
                : tool === "polygon"
                ? "Click points; click near the first point or double-click to close."
                : "Click a shape to select. Drag to move, drag corners to resize."}
            </div>
          </div>
          <div className="flex-1">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
              className="w-full border border-border rounded bg-background select-none"
              style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}` }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onDoubleClick={onDoubleClick}
            >
              <defs>
                <pattern id="dc-grid" width={GRID} height={GRID} patternUnits="userSpaceOnUse">
                  <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke="oklch(0.9 0.01 250)" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width={CANVAS_W} height={CANVAS_H} fill="url(#dc-grid)" />
              <g dangerouslySetInnerHTML={{ __html: innerMarkup }} />
              {selected && (selected.kind === "rect" || selected.kind === "svg") && (
                <>
                  <rect x={selected.x - 1} y={selected.y - 1} width={selected.w + 2} height={selected.h + 2}
                    fill="none" stroke="oklch(0.6 0.2 250)" strokeDasharray="4 3" strokeWidth={1.5} pointerEvents="none" />
                  {tool === "select" && ([
                    { c: "nw" as const, x: selected.x, y: selected.y, cursor: "nwse-resize" },
                    { c: "ne" as const, x: selected.x + selected.w, y: selected.y, cursor: "nesw-resize" },
                    { c: "sw" as const, x: selected.x, y: selected.y + selected.h, cursor: "nesw-resize" },
                    { c: "se" as const, x: selected.x + selected.w, y: selected.y + selected.h, cursor: "nwse-resize" },
                  ]).map((h) => (
                    <rect key={h.c} x={h.x - 5} y={h.y - 5} width={10} height={10}
                      fill="white" stroke="oklch(0.6 0.2 250)" strokeWidth={1.5}
                      style={{ cursor: h.cursor }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        const p = rawLocal(e);
                        setDrag({ id: selected.id, mode: "resize", corner: h.c, start: p, orig: { x: selected.x, y: selected.y, w: selected.w, h: selected.h } });
                      }}
                    />
                  ))}
                </>
              )}
              {liveRect && (
                <rect x={liveRect.x} y={liveRect.y} width={liveRect.w} height={liveRect.h} fill={fill} fillOpacity={0.4} stroke={stroke} strokeDasharray="4 3" strokeWidth={2} />
              )}
              {polyPts.length > 0 && (
                <>
                  <polyline
                    points={[...polyPts, hoverPt ?? polyPts[polyPts.length - 1]].map((p) => `${p.x},${p.y}`).join(" ")}
                    fill={fill}
                    fillOpacity={0.25}
                    stroke={stroke}
                    strokeDasharray="4 3"
                    strokeWidth={2}
                  />
                  {polyPts.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={3} fill={stroke} />
                  ))}
                </>
              )}
            </svg>
          </div>
          <div className="w-48 flex flex-col gap-1 border-l border-border pl-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Layers ({shapes.length})</div>
            <div className="flex-1 overflow-auto max-h-80 space-y-0.5">
              {orderedShapes.length === 0 && <div className="text-[11px] text-muted-foreground italic">No shapes yet</div>}
              {[...orderedShapes].reverse().map((s) => (
                <div key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className={`flex items-center gap-1 text-[11px] px-1.5 py-1 rounded cursor-pointer ${selectedId === s.id ? "bg-accent" : "hover:bg-accent/50"}`}>
                  <span className="w-3 h-3 rounded-sm border border-border shrink-0" style={{ background: s.fill }} />
                  <span className="flex-1 truncate">{s.kind === "svg" ? s.name : s.role}</span>
                  <button onClick={(e) => { e.stopPropagation(); moveLayer(s.id, 1); }} className="opacity-60 hover:opacity-100" title="Bring forward"><ChevronUp className="w-3 h-3" /></button>
                  <button onClick={(e) => { e.stopPropagation(); moveLayer(s.id, -1); }} className="opacity-60 hover:opacity-100" title="Send back"><ChevronDown className="w-3 h-3" /></button>
                  <button onClick={(e) => { e.stopPropagation(); toggleHide(s.id); }} className="opacity-60 hover:opacity-100" title="Toggle visibility">
                    {s.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); removeShape(s.id); }} className="opacity-60 hover:opacity-100 text-destructive" title="Delete"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            {selected && selected.kind !== "svg" && (
              <div className="border-t border-border pt-2 mt-1 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Selected label</Label>
                <Select value={selected.role} onValueChange={(v) => updateShape(selected.id, { role: v as Role } as Partial<Shape>)}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-1.5">
                  <div className="flex-1">
                    <Label className="text-[9px] uppercase text-muted-foreground">Fill</Label>
                    <input type="color" value={selected.fill} onChange={(e) => updateShape(selected.id, { fill: e.target.value } as Partial<Shape>)} className="w-full h-7 rounded border border-border bg-transparent" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[9px] uppercase text-muted-foreground">Stroke</Label>
                    <input type="color" value={selected.stroke} onChange={(e) => updateShape(selected.id, { stroke: e.target.value } as Partial<Shape>)} className="w-full h-7 rounded border border-border bg-transparent" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-12 gap-3 pt-2 items-end">
          <div className="col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Units</Label>
            <Select value={units} onValueChange={(v) => setUnits(v as "ft" | "cm")}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ft" className="text-xs">ft + in</SelectItem>
                <SelectItem value="cm" className="text-xs">cm</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {units === "cm" ? (
            <>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Width (cm)</Label>
                <Input type="number" value={cmW} onChange={(e) => setCmW(Math.max(1, Number(e.target.value) || 0))} className="h-8" />
                <div className="text-[10px] text-muted-foreground mt-0.5">≈ {cmWInFt.ft}′ {cmWInFt.in}″</div>
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Height (cm)</Label>
                <Input type="number" value={cmH} onChange={(e) => setCmH(Math.max(1, Number(e.target.value) || 0))} className="h-8" />
                <div className="text-[10px] text-muted-foreground mt-0.5">≈ {cmHInFt.ft}′ {cmHInFt.in}″</div>
              </div>
            </>
          ) : (
            <>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Width</Label>
                <div className="flex gap-1">
                  <Input type="number" min={0} value={cmWInFt.ft} onChange={(e) => setCmW(ftInToCm(Number(e.target.value) || 0, cmWInFt.in))} className="h-8" placeholder="ft" />
                  <Input type="number" min={0} step={0.5} value={cmWInFt.in} onChange={(e) => setCmW(ftInToCm(cmWInFt.ft, Number(e.target.value) || 0))} className="h-8" placeholder="in" />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">= {cmW} cm</div>
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Height</Label>
                <div className="flex gap-1">
                  <Input type="number" min={0} value={cmHInFt.ft} onChange={(e) => setCmH(ftInToCm(Number(e.target.value) || 0, cmHInFt.in))} className="h-8" placeholder="ft" />
                  <Input type="number" min={0} step={0.5} value={cmHInFt.in} onChange={(e) => setCmH(ftInToCm(cmHInFt.ft, Number(e.target.value) || 0))} className="h-8" placeholder="in" />
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">= {cmH} cm</div>
              </div>
            </>
          )}
        </div>
        <DialogFooter className="gap-2">
          {!session && (
            <Button asChild variant="ghost" size="sm" className="text-xs text-muted-foreground">
              <Link href="#">Sign in to sync to cloud</Link>
            </Button>
          )}
          <Button onClick={onSave} disabled={create.isPending || !shapes.length}>
            {create.isPending ? "Saving…" : session ? "Save to library" : "Save locally"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}